<script setup lang="ts">
import { STATUS_CONSTANTS } from "./swiperMethods/statusConstants";
import type { ISwiperCardProps } from "~/types/swipe-bet/swiper-card";

const props = withDefaults(defineProps<ISwiperCardProps>(), {
	tinderMounted: false,
	ready: false,
	ratio: 0,
	rewind: false,
});

const scopedRewind: Ref<boolean | number> = ref(false);
const initiated = ref(false);

const isCur = computed(() => {
	return props.index === 0;
});

const style = computed(() => {
	if (!initiated.value) {
		return {};
	}

	if (props.swiperState.status === STATUS_CONSTANTS.MOVING) {
		return movingStyle.value;
	}
	return normalStyle.value;
});

const normalStyle = computed(() => {
	if (isCur.value) {
		return {
			opacity: 1,
			transform: "translate3d(0,0,0) rotate(0) scale3d(1,1,1)",
			transition: "all 500ms cubic-bezier(0.175, 0.885, 0.32, 1.275), z-index 0s",
		};
	}
	return {
		opacity: props.ready ? 0 : 1,
		transform: getTransform(),
		transition: `all 500ms cubic-bezier(0.175, 0.885, 0.32, 1.275) ${
			scopedRewind.value && typeof scopedRewind.value === "number" ? scopedRewind.value * 80 : 0
		}ms, z-index 0s`,
	};
});

const movingStyle = computed(() => {
	const style: { [key: string]: string } = { transition: "none" };
	if (isCur.value) {
		const { start, move, startPoint } = props.swiperState;
		const x = move.x - start.x || 0;
		const y = move.y - start.y || 0;
		const rotate = 10 * props.ratio * startPoint;
		style.transform = `translate3d(${x}px,${y}px,0) rotate(${rotate}deg)`;
	} else {
		let ratio = Math.abs(props.ratio);
		if (ratio > 1) {
			ratio = 1;
		}
		if (props.ready) {
			style.opacity = (ratio * 1).toString();
		}
		style.transform = getTransform(ratio);
	}
	return style;
});

onBeforeMount(() => {
	scopedRewind.value = props.rewind;
	if (!props.tinderMounted) {
		initiated.value = true;
	}
});

onMounted(() => {
	requestAnimationFrame(() => {
		initiated.value = true;
	});
});

const emit = defineEmits(["reverted"]);
const transitionEnd = (e: TransitionEvent) => {
	if (e.target === e.currentTarget && e.propertyName === "transform") {
		// Hide rewind slot after animation
		scopedRewind.value = false;
		if (isCur.value) {
			const status = props.swiperState.status;
			if (status === STATUS_CONSTANTS.REVERT || status === STATUS_CONSTANTS.REWINDING) {
				emit("reverted");
			}
		}
	}
};

const getTransform = (ratio?: number) => {
	const index = props.index;
	let translateY: number | string = 0;
	let scale = 1 - props.scaleStep * index;
	if (ratio) {
		scale += ratio * props.scaleStep;
	}
	if (props.offsetY) {
		const inverse = props.offsetY < 0;
		const offsetY = Math.abs(props.offsetY);
		let y = index * offsetY;
		let offsetScale = ((1 - scale) / 2) * 100;
		if (ratio) {
			y -= ratio * offsetY;
		}
		if (inverse) {
			y *= -1;
			offsetScale *= -1;
		}
		translateY = `calc(${offsetScale}% + ${y}${props.offsetUnit})`;
	}
	return `translate3d(0,${translateY},0) scale3d(${scale},${scale},1)`;
};

watch(
	() => props.index,
	(val, oldVal) => {
		if (val < oldVal) {
			scopedRewind.value = false;
		}
	}
);

watch(
	() => props.rewind,
	(val) => {
		scopedRewind.value = val;
	}
);

watch(
	() => props.swiperState.status,
	(status) => {
		if (status === STATUS_CONSTANTS.REWINDING) {
			// Set rewind delay for animation synchronization
			scopedRewind.value = props.index;
		}
	}
);
</script>

<template>
	<div
		:data-index="index"
		:data-id="$attrs['data-id']"
		class="absolute w-full h-full overflow-hidden rounded-2xl shadow-xl dark:shadow-lg dark:shadow-[#00A82645]"
		:style="[{ zIndex: 100 - index }, style]"
		@transitionend="transitionEnd"
	>
		<slot />
		<slot name="nope" />
		<slot name="like" />
		<slot name="super" />
		<slot name="down" />
		<transition name="swiper-rewind">
			<slot name="rewind" v-if="scopedRewind !== false" />
		</transition>
	</div>
</template>

<style scoped>
.swiper-rewind-leave-active {
	transition: all 0.5s ease;
}

.swiper-rewind-leave-to {
	opacity: 0;
}
</style>
